<script lang="ts">
    import {
        VpArrowDownIcon,
        VpArrowRightIcon,
        VpArrowUpIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createPagination } from '@melt-ui/svelte';
    import dayjs from 'dayjs';
    import { fade } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import { formatTourDate, formatTourDate2 } from '$saga/utils/date';
    import { formatCurrency } from '$saga/utils/price';
    import {
        datesInfos,
        isModalOpen,
        modalStep,
        selectedPrice,
    } from '$saga/views/Tour/store';
    import AgeRangePricesDrawer from '$saga/views/Tour/VisibleDates/AgeRangePricesDrawer.svelte';
    import MinPricePerMonthsList from '$saga/views/Tour/VisibleDates/MinPricePerMonthsList.svelte';
    import type {
        MinPriceMonth,
        MonthlyPrices,
        VisibleDate,
    } from '$saga/views/Tour/VisibleDates/types';
    import UnavailableTour from '$saga/views/Tour/VisibleDates/UnavailableTour.svelte';
    import {
        getCurrentOrNextAvailableMonth,
        isDateRemainingStockAvailable,
        isDateRemainingStockLast,
    } from '$saga/views/Tour/VisibleDates/utils';

    export let tourDatesInfo: string,
        visibleDates: VisibleDate[],
        searchUrl: string,
        isBookable: boolean,
        cheapestMonthlyPrices: MonthlyPrices,
        hideStock: boolean = false;

    const minPricePerMonths: MinPriceMonth[] = Object.keys(
        cheapestMonthlyPrices,
    ).map((key) => ({
        // key is the date
        date: dayjs(key),
        price: cheapestMonthlyPrices[key]?.amount,
        quantile: cheapestMonthlyPrices[key]?.quantile,
    }));
    let isShowingMore = false;
    let currentActiveMonth = getCurrentOrNextAvailableMonth(
        minPricePerMonths,
        visibleDates,
    );
    let filteredDatesByMonth: VisibleDate[] = visibleDates.filter(
        (visibleDate) =>
            dayjs(visibleDate.date1.date).isSame(currentActiveMonth, 'month'),
    );

    $: {
        filteredDatesByMonth = visibleDates.filter((visibleDate) =>
            dayjs(visibleDate.date1.date).isSame(currentActiveMonth, 'month'),
        );
        // reset showmore button at every currentActiveMonth change
        isShowingMore = false;
    }

    const {
        elements: { root, prevButton, nextButton },
        states: { range, totalPages, page },
    } = createPagination({
        count: minPricePerMonths.length - 1,
        perPage: 5,
    });

    const handleSelectedDate = (event: Event, date: VisibleDate) => {
        const target = (
            event.currentTarget as HTMLButtonElement
        )?.querySelector('input[name="startDateTable"]');
        // avoid event to be retriggered by the input
        if (
            target &&
            (event.target as HTMLButtonElement)?.tagName !== 'INPUT'
        ) {
            (target as HTMLInputElement).click();
        }
        //send selected date to store
        $selectedPrice = {
            originalPrice: date.hasRebate ? date.price : null,
            rebateAmount:
                date.hasRebate && date.rebateAmount
                    ? Math.round(date.rebateAmount / 100)
                    : null,
            actualPrice:
                date.hasRebate && date.rebatedPrice
                    ? date.rebatedPrice
                    : date.price,
        };

        $datesInfos = {
            price: date.hasRebate ? date.rebatedPrice : date.price,
            remainingStock: date?.remaining_stock ?? '',
            statusName: date?.statusName ?? '',
            selectedDate: dayjs(date.date1.date).format('YYYY-MM-DD'),
        };
        $modalStep = 2;
        $isModalOpen = true;
    };
</script>

<div>
    {#if visibleDates.length > 0}
        <h2 class="pb-m text-[32px] font-semibold text-content-neutral">
            {trans('tour.dates.title')}
        </h2>
        <div class="w-full bg-white p-m text-content-neutral">
            <div class="mb-2xl flex flex-col gap-m">
                <h3 class="text-[22px] font-semibold">
                    {trans('tour.dates.start')}
                </h3>
                <span class="text-center font-semibold">
                    {trans('tour.dates.months_select')}
                </span>
                <MinPricePerMonthsList
                    {root}
                    {prevButton}
                    {nextButton}
                    {page}
                    {range}
                    bind:currentActiveMonth
                    {totalPages}
                    {minPricePerMonths}
                    {visibleDates}
                />
            </div>

            <div class="mt-m flex flex-col gap-s">
                {#each filteredDatesByMonth as date, index (date.id)}
                    <button
                        in:fade
                        class={cn(
                            'flex w-full rounded-lg border border-solid border-border-quiet p-s hover:border-border-brand hover:bg-extended-cobalt-quiet',
                            !isShowingMore && index > 4 && 'hidden',
                            !date.isFull && 'cursor-pointer',
                            !dayjs(date.date1.date).isSame(
                                currentActiveMonth,
                                'month',
                            ) && 'hidden',
                        )}
                        disabled={date.isFull}
                        on:click={(event) =>
                            !date.isFull && handleSelectedDate(event, date)}
                    >
                        <span
                            class="flex w-full flex-col gap-xs text-left desktop:flex-row desktop:items-center"
                        >
                            <span
                                class={cn(
                                    'w-full',
                                    date.isFull && 'opacity-[0.38]',
                                )}
                            >
                                {trans('tour.dates.from')}
                                <span class="font-semibold">
                                    {formatTourDate(date.date1.date)}
                                </span>
                                {trans('tour.dates.to')}
                                <span class="font-semibold">
                                    {formatTourDate2(date.date2.date)}
                                </span>
                            </span>
                            {#if date.isFull}
                                <div class="flex w-full flex-col gap-2xs">
                                    <span class="font-semibold"
                                        >{date.statusName}</span
                                    >
                                    <span
                                        class="text-sm text-content-brand opacity-[0.38]"
                                    >
                                        Indisponible
                                    </span>
                                </div>
                            {:else if !hideStock}
                                <div class="flex w-full flex-col gap-2xs">
                                    <span
                                        class="font-semibold text-content-brand"
                                    >
                                        {date.statusName}
                                    </span>
                                    {#if isDateRemainingStockAvailable(date)}
                                        <span class="text-sm">
                                            {date.remaining_stock}
                                            {isDateRemainingStockLast(date)
                                                ? trans(
                                                      'tour.dates.spot_plural',
                                                  )
                                                : trans(
                                                      'tour.dates.spot_plural',
                                                  )}

                                            {isDateRemainingStockLast(date)
                                                ? trans(
                                                      'tour.dates.availability',
                                                  )
                                                : trans(
                                                      'tour.dates.availability_plural',
                                                  )}

                                            {#if date.paxSoldCount > 0}
                                                <span
                                                    >- {date.paxSoldCount}
                                                    {trans(
                                                        'tour.dates.registered',
                                                    )}</span
                                                >
                                            {/if}
                                        </span>
                                    {/if}
                                </div>
                            {/if}
                            <span
                                class={cn(
                                    'flex w-full items-center justify-end gap-xs',
                                    date.isFull && 'opacity-[0.38]',
                                )}
                            >
                                {#if !isBookable}
                                    <span class="text-xs"
                                        >{trans('tour.dates.price_from')}</span
                                    >
                                {/if}
                                {#if date.hasRebate && date.rebateAmount}
                                    <span
                                        class="bg-status-negative p-2xs font-semibold text-white"
                                        data-date={dayjs(
                                            date.date1.date,
                                        ).format('YYYY-MM-DD')}
                                    >
                                        {formatCurrency(
                                            date.rebatedPrice ?? 0,
                                            false,
                                            'EUR',
                                            true,
                                        )}
                                    </span>
                                    <div class="flex flex-col gap-2xs">
                                        <span
                                            class="text-xs text-content-quiet line-through"
                                        >
                                            {formatCurrency(date.price, false)}
                                        </span>
                                        <span
                                            class="text-xs text-status-negative"
                                        >
                                            -{formatCurrency(
                                                Math.round(
                                                    date.rebateAmount / 100,
                                                ),
                                                false,
                                            )}
                                        </span>
                                    </div>
                                {:else}
                                    <span
                                        class="bg-container-commercial-catchy p-2xs font-semibold"
                                        data-date={dayjs(
                                            date.date1.date,
                                        ).format('YYYY-MM-DD')}
                                    >
                                        {formatCurrency(date.price, true)}
                                    </span>
                                {/if}
                                {#if date.ageRangePrices.length > 1}
                                    <AgeRangePricesDrawer
                                        disabled={date.isFull}
                                        ageRangePrices={date.ageRangePrices}
                                    />
                                {/if}
                            </span>
                        </span>
                        <span class="m-auto ml-xl h-full">
                            <VpArrowRightIcon
                                class={cn(
                                    'size-l text-content-neutral',
                                    date.isFull &&
                                        'text-content-quiet opacity-[0.38]',
                                )}
                            />
                        </span>
                    </button>
                {:else}
                    <div in:fade class="mt-xs text-center flex flex-col gap-xs">
                        <span class="font-semibold">
                            {trans('tour.dates.empty.title')}
                        </span>
                        <span class="text-sm">
                            {trans('tour.dates.empty.description')}
                        </span>
                    </div>
                {/each}
            </div>

            {#if filteredDatesByMonth.length > 5}
                <div class="my-m flex items-center justify-center">
                    <Button
                        intent="tertiary"
                        size="small"
                        endIcon={!isShowingMore
                            ? VpArrowDownIcon
                            : VpArrowUpIcon}
                        on:click={() => (isShowingMore = !isShowingMore)}
                    >
                        {#if !isShowingMore}
                            {trans('tour.dates.showmore')}
                        {:else}
                            {trans('tour.dates.showless')}
                        {/if}
                    </Button>
                </div>
            {/if}
        </div>
    {:else}
        <UnavailableTour {searchUrl} />
    {/if}
    {#if tourDatesInfo}
        <div class="info">
            <h3 class="heading">{trans('tour.dates.info')}</h3>
            <div class="content is-rt">
                <!--  eslint-disable-next-line svelte/no-at-html-tags -->
                {@html tourDatesInfo}
            </div>
        </div>
    {/if}
</div>
